// Refactored: AI Service Page
import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MemoryIcon from "@mui/icons-material/Memory";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PsychologyIcon from "@mui/icons-material/Psychology";
import InsightsIcon from "@mui/icons-material/Insights";

import { useNavigate } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { motion } from "framer-motion";

// ✅ AI Content Data
const aiContent = [
  {
    section: "Machine Learning",
    icon: <MemoryIcon />,
    items: [
      {
        name: "Supervised Learning",
        description: "Predict outcomes using labeled datasets.",
        fullContent: [
          {
            heading: "Overview",
            text: `Supervised learning is a fundamental machine learning technique where models are trained using labeled data. 
Each training example contains both the input variables and the correct output, which the algorithm tries to learn. 
The objective is to minimize the difference between predictions and actual labels.`
          },
          {
            heading: "Key Techniques",
            points: [
              "Linear Regression – predicts continuous values.",
              "Logistic Regression – binary classification tasks.",
              "Decision Trees – interpretable models with branches.",
              "Random Forests – ensemble of trees for better accuracy.",
              "Support Vector Machines – separates classes with maximum margin."
            ]
          },
          {
            heading: "Example Code",
            code: `# Example: Linear Regression (Basic)
from sklearn.linear_model import LinearRegression
import numpy as np

X = np.array([[1],[2],[3],[4]])
y = np.array([2,4,6,8])

model = LinearRegression()
model.fit(X, y)
print("Prediction for 5:", model.predict([[5]]))`
          }
        ],
      },
      {
        name: "Unsupervised Learning",
        description: "Find patterns in unlabeled data.",
        fullContent: [
          {
            heading: "Overview",
            text: `Unsupervised learning deals with datasets that have no labels, meaning the algorithm must find structure on its own. 
Instead of predicting outcomes, it tries to group or organize data. Clustering is a major approach where similar data points are grouped together.`
          },
          {
            heading: "Techniques",
            points: [
              "K-Means Clustering – groups data into k clusters.",
              "Hierarchical Clustering – builds a tree of clusters.",
              "DBSCAN – detects clusters based on density.",
              "PCA – reduces dimensions while preserving variance."
            ]
          },
          {
            heading: "Example Code",
            code: `# Example: KMeans Clustering
from sklearn.cluster import KMeans
import numpy as np

X = np.array([[1,2],[1,4],[1,0],[10,2],[10,4],[10,0]])
kmeans = KMeans(n_clusters=2, random_state=0).fit(X)
print("Cluster labels:", kmeans.labels_)`
          }
        ],
      }
    ],
  },
  {
    section: "Deep Learning",
    icon: <AutoAwesomeIcon />,
    items: [
      {
        name: "Neural Networks",
        description: "Simulate human brain to recognize patterns.",
        fullContent: [
          {
            heading: "Overview",
            text: `Neural networks are inspired by the human brain and consist of layers of interconnected nodes (neurons). 
They can learn complex patterns by passing data through multiple layers. Each neuron applies a weighted sum followed by an activation function.`
          },
          {
             heading: "Architecture",
             points: [
                 "Input Layer: Receives raw data.",
                 "Hidden Layers: Perform computations to extract features.",
                 "Output Layer: Delivers the final prediction."
             ]
          },
          {
            heading: "Basic Neural Network with Keras",
            code: `import tensorflow as tf
from tensorflow.keras.models import Sequential
from tensorflow.keras.layers import Dense

model = Sequential([
    Dense(16, activation="relu", input_shape=(10,)),
    Dense(1, activation="sigmoid")
])

model.compile(optimizer="adam", loss="binary_crossentropy")
print(model.summary())`
          }
        ],
      }
    ],
  },
  {
    section: "Natural Language Processing",
    icon: <PsychologyIcon />,
    items: [
      {
        name: "Text Classification",
        description: "Categorize text into predefined classes.",
        fullContent: [
          {
            heading: "Overview",
            text: `Natural Language Processing (NLP) focuses on enabling computers to understand and process human language. 
Text classification is one of its most common tasks, where text is assigned to categories (e.g., spam detection, sentiment analysis).`
          },
          {
            heading: "Common Models",
            points: [
                "Bag-of-Words & TF-IDF",
                "Word Embeddings (Word2Vec, GloVe)",
                "Transformers (BERT, GPT)"
            ]
          },
          {
            heading: "Text Classification with Naive Bayes",
            code: `from sklearn.feature_extraction.text import CountVectorizer
from sklearn.naive_bayes import MultinomialNB

docs = ["I love AI", "AI is amazing", "I hate bugs"]
y = [1, 1, 0]

vectorizer = CountVectorizer()
X = vectorizer.fit_transform(docs)

clf = MultinomialNB()
clf.fit(X, y)`
          }
        ],
      }
    ],
  },
  {
    section: "AI Tools",
    icon: <InsightsIcon />,
    items: [
      {
        name: "TensorFlow",
        description: "End-to-end open-source platform for ML.",
        fullContent: [
          {
            heading: "Overview",
            text: `TensorFlow is a powerful open-source framework developed by Google for building machine learning and deep learning models. 
It provides a comprehensive ecosystem for model development, training, and deployment.`
          },
          {
            heading: "Core Usage",
            code: `import tensorflow as tf
hello = tf.constant("Hello, TensorFlow!")
print(hello.numpy())`
          }
        ],
      }
    ],
  }
];

function ArtificialIntelligence() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box mb={4} display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              fontWeight: 700,
              color: "#034522",
              mr: 2,
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              "&:hover": { bgcolor: "rgba(3, 69, 34, 0.1)" },
            }}
          >
            Back to Services
          </Button>
          <Typography variant="h4" fontWeight={800} color="#1a1a1a" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            Artificial Intelligence
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar Tabs (Desktop) / Top Tabs (Mobile) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                background: "#fff",
              }}
            >
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="scrollable"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: "divider",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1A9E7F",
                    width: "4px",
                    left: { md: 0 },
                    right: "auto"
                  },
                }}
              >
                {aiContent.map((section, index) => (
                  <Tab
                    key={index}
                    icon={<Box sx={{ color: activeTab === index ? "#1A9E7F" : "inherit" }}>{section.icon}</Box>}
                    iconPosition="start"
                    label={section.section}
                    sx={{
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      color: activeTab === index ? "#034522" : "#666",
                      fontSize: "1rem",
                      py: 3,
                      px: 3,
                      minHeight: 64,
                      transition: "all 0.3s",
                      "&.Mui-selected": {
                        color: "#034522",
                        bgcolor: "rgba(26, 158, 127, 0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  minHeight: "60vh",
                  bgcolor: "#fff"
                }}
              >
                <Box mb={4} display="flex" alignItems="center">
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: 'rgba(3, 69, 34, 0.1)', 
                            color: '#034522',
                            mr: 2,
                            display: 'flex',
                            boxShadow: '0 4px 12px rgba(3, 69, 34, 0.1)'
                        }}
                    >
                        {aiContent[activeTab].icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#034522">
                    {aiContent[activeTab].section}
                    </Typography>
                </Box>
                
                <Box>
                  {aiContent[activeTab].items.map((item, i) => (
                    <Accordion
                      key={i}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid #edf2f7",
                        "&:before": { display: "none" },
                        "&:hover": { bgcolor: "#f8f9fa", borderColor: "#e2e8f0" },
                        transition: "all 0.2s"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#1A9E7F" }} />}
                        sx={{ px: 3, py: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#1a1a1a' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 4, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                        {item.fullContent.map((content, idx) => (
                          <Box key={idx} mt={3}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              color="#1A9E7F"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9E7F' }} />
                                {content.heading}
                            </Typography>
                            {content.text && (
                              <Typography paragraph variant="body2" color="#4a5568" mb={2} sx={{ lineHeight: 1.7 }}>
                                {content.text}
                              </Typography>
                            )}
                            {content.points && (
                                <ul style={{ marginLeft: "1.5rem", marginTop: 0, marginBottom: '16px', color: '#4a5568' }}>
                                    {content.points.map((point, pIdx) => (
                                        <li key={pIdx}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{point}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {content.code && (
                              <Box
                                sx={{
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  border: "1px solid #e2e8f0",
                                  marginTop: 1,
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                                }}
                              >
                                <SyntaxHighlighter
                                  language="python"
                                  style={atomDark}
                                  customStyle={{ margin: 0, padding: "20px", fontSize: "0.9rem", lineHeight: 1.5 }}
                                >
                                  {content.code}
                                </SyntaxHighlighter>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default ArtificialIntelligence;
